import { SSEParser } from './SSEParser';
import type { SSEMessage } from './SSEParser';
import { sseEventDispatcher } from './sse-event-dispatcher';
import { API_BASE as BACKEND_BASE } from '@/config';
import type {
  AgentExecuteRequest,
  AgentExecuteResponse,
  SkillInfo,
  SearchResponse,
  AgentProfile,
  AgentListResponse,
} from '@/types/agent';

const API_BASE = `${BACKEND_BASE}/api/agent`;

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120_000); // 2 分钟超时

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `Request failed: ${response.status}`);
    }

    return response.json();
  } catch (err: any) {
    if (err.name === 'AbortError') {
      throw new Error('请求超时，请检查网络连接或稍后重试');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

function processSSEMessage(msg: SSEMessage): { type: string; content?: string } | null {
  if (msg.data === '[DONE]') {
    return { type: 'done' };
  }

  if (msg.event === 'error') {
    return { type: 'error', content: msg.data };
  }

  try {
    const data = JSON.parse(msg.data);
    return data;
  } catch {
    if (msg.data) {
      return { type: 'text', content: msg.data };
    }
    return null;
  }
}

interface ChatStreamOptions {
  sessionId?: string;
  projectId?: string;
  modelId?: string;
  attachments?: any[];
  references?: any[];
  searchMode?: 'auto' | 'fast' | 'deep';
  agentId?: string;
  onMessageIds?: (ids: { user_msg_id: string; assistant_msg_id: string }) => void;
  onAgentId?: (agentId: string) => void;
  onThinking?: (content: string) => void;
  onStatus?: (status: string) => void;
  onCollabStatus?: (content: string) => void;
  onIntent?: (intent: { name: string; confidence: number; source: string; params?: Record<string, any> }) => void;
  onSubThinking?: (agentId: string, content: string) => void;
  onSubOutput?: (agentId: string, content: string) => void;
  onClarificationRequest?: (data: { callId: string; question: string; options: string[] }) => void;
  onGenerationConfirm?: (data: { content: string; generationOptions: any; intent: any; messageType: string; assistantMsgId?: string }) => void;
  onSearchResult?: (data: any) => void;
}

function dispatchSSEEvent(
  event: { type: string; content?: string },
  options: ChatStreamOptions | undefined,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void
) {
  sseEventDispatcher.dispatch(event, options, onChunk, onDone, onError);
}

export const AgentService = {
  async execute(req: AgentExecuteRequest): Promise<AgentExecuteResponse> {
    return request('/execute', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async chatStream(
    messages: { role: string; content: string }[],
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
    options?: ChatStreamOptions
  ): Promise<AbortController> {
    const controller = new AbortController();
    const parser = new SSEParser();

    try {
      const body: Record<string, any> = {
        messages,
        session_id: options?.sessionId,
        project_id: options?.projectId,
        model_id: options?.modelId,
      };
      if (options?.attachments?.length) {
        body.attachments = options.attachments;
      }
      if (options?.references?.length) {
        body.references = options.references;
      }
      if (options?.searchMode) {
        body.search_mode = options.searchMode;
      }
      if (options?.agentId) {
        body.agent_id = options.agentId;
      }

      const response = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Stream failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();

      const read = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const sseMessages = parser.feed(chunk);

            for (const msg of sseMessages) {
              const event = processSSEMessage(msg);
              if (!event) continue;
              dispatchSSEEvent(event, options, onChunk, onDone, onError);
            }
          }

          const remaining = parser.flush();
          if (remaining) {
            const event = processSSEMessage(remaining);
            if (event) {
              dispatchSSEEvent(event, options, onChunk, onDone, onError);
            }
          }
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            onError(err.message || 'Stream error');
          }
        }
      };

      read();
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        onError(err.message || 'Connection error');
      }
    }

    return controller;
  },

  async listSkills(): Promise<SkillInfo[]> {
    return request('/skills');
  },

  async toggleSkill(skillId: string, enabled: boolean): Promise<void> {
    await request(`/skills/${skillId}/toggle?enabled=${enabled}`, { method: 'POST' });
  },

  async submitFeedback(messageId: string, sessionId: string, rating: number, comment?: string, category?: string): Promise<void> {
    await request('/feedback', {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        session_id: sessionId,
        rating,
        comment,
        category,
      }),
    });
  },

  // P3-4: search 方法已移除，搜索功能统一通过 chatStream 流式路径执行
  // 使用 SearchOrchestrator 作为唯一搜索入口

  async createSession(projectId: string): Promise<{ sessionId: string; projectId: string; status: string }> {
    return request('/sessions', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    });
  },

  async getSessionMessages(sessionId: string, limit: number = 50): Promise<{
    sessionId: string;
    messages: Array<{
      id: string;
      sessionId: string;
      role: string;
      content: string;
      type: string;
      isComplete?: number;
      sequence?: number;
      createdAt: string;
      updatedAt?: string;
      metadata?: Record<string, any>;
      introText?: string;
      processLogs?: any[];
      attachments?: any[];
      references?: any[];
    }>;
    total: number;
    messageCount?: number;
    version?: number;
  }> {
    return request(`/sessions/${sessionId}/messages?limit=${limit}`);
  },

  async closeSession(sessionId: string): Promise<{ success: boolean; sessionId: string; status: string }> {
    return request(`/sessions/${sessionId}/close`, { method: 'POST' });
  },

  async updateMessageMetadata(
    messageId: string,
    sessionId: string,
    metadata: Record<string, any>,
    // 🔴 v6-#3: 支持原子更新 content 和 msg_type，避免两次 API 调用之间的数据不一致
    extra?: { content?: string; msg_type?: string }
  ): Promise<{ success: boolean; messageId: string }> {
    return request('/messages/metadata', {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        session_id: sessionId,
        metadata,
        ...(extra?.content !== undefined ? { content: extra.content } : {}),
        ...(extra?.msg_type !== undefined ? { msg_type: extra.msg_type } : {}),
      }),
    });
  },

  async deleteMessage(
    messageId: string,
    sessionId: string
  ): Promise<{ success: boolean; messageId: string }> {
    return request(`/messages/${messageId}?session_id=${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    });
  },

  async updateMessageContent(
    messageId: string,
    sessionId: string,
    content: string,
    type?: string
  ): Promise<{ success: boolean; messageId: string }> {
    const body: Record<string, any> = { session_id: sessionId, content };
    if (type) body.type = type;
    return request(`/messages/${messageId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getStreamStatus(sessionId: string, assistantMsgId?: string): Promise<{
    state: string;
    taskId?: string;
    content?: string;
    contentLength?: number;
    isComplete?: number;
    error?: string;
    processLogs?: any[];
    introText?: string;
  }> {
    const params = new URLSearchParams();
    params.set('session_id', sessionId);
    if (assistantMsgId) params.set('assistant_msg_id', assistantMsgId);
    return request(`/chat/stream-status?${params.toString()}`);
  },

  async getIncompleteMessages(sessionId: string): Promise<{
    messages: Array<{
      id: string;
      content: string;
      isComplete: number;
      type: string;
      updatedAt: string | null;
    }>;
  }> {
    return request(`/sessions/${sessionId}/incomplete-messages`);
  },

  async listSessions(projectId?: string, limit: number = 20): Promise<{
    sessions: Array<{
      sessionId: string;
      projectId: string;
      status: string;
      messageCount?: number;
      version?: number;
      topic?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('limit', String(limit));
    return request(`/sessions?${params.toString()}`);
  },

  async listAgents(): Promise<AgentListResponse> {
    return request('/agents');
  },

  async getAgent(agentId: string): Promise<AgentProfile> {
    return request(`/agents/${agentId}`);
  },

  async reloadAgents(): Promise<{ success: boolean; agents_count: number }> {
    return request('/agents/reload', { method: 'POST' });
  },
};
